import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ISSUES } from "@/data/site";
import { CAST } from "@/data/characters";
import { CTA } from "@/components/CTA";
import { Reveal, MaskLines } from "@/components/Reveal";
import { Kicker } from "@/components/Section";

const castBySlug = Object.fromEntries(CAST.map((c) => [c.slug, c]));

const Barcode = () => (
  <div className="flex h-10 items-end gap-[2px]" aria-hidden="true">
    {[3, 1, 2, 1, 4, 2, 1, 3, 1, 2, 4, 1, 2, 3, 1, 1, 2, 4, 1, 3].map((w, i) => (
      <div key={i} className="bg-silver/80" style={{ width: `${w}px`, height: `${60 + ((i * 13) % 40)}%` }} />
    ))}
  </div>
);

const ANCHORS = [
  { id: "brief", label: "Brief" },
  { id: "concept", label: "Concept" },
  { id: "campaign", label: "Campaign" },
  { id: "crew", label: "Crew" },
  { id: "bts", label: "BTS" },
  { id: "reflection", label: "Reflection" },
  { id: "outcomes", label: "Outcomes" },
  { id: "credits", label: "Credits" },
];

const IssueSection = ({ id, kicker, children, dark = false }) => (
  <section id={id} className={`grain-dark scroll-mt-24 border-b border-gold/20 py-16 text-cream sm:py-20 ${dark ? "bg-charcoal" : "bg-onyx"}`}>
    <div className="relative mx-auto max-w-[1100px] px-4 sm:px-6">
      <Reveal><Kicker>{kicker}</Kicker></Reveal>
      <div className="mt-8">{children}</div>
    </div>
  </section>
);

export default function CampaignDetail() {
  const { slug } = useParams();
  const issue = ISSUES.find((i) => i.slug === slug || i.slug.endsWith(`-${slug}`) || slug === i.number);
  const idx = ISSUES.indexOf(issue);
  const next = idx >= 0 ? ISSUES[(idx + 1) % ISSUES.length] : null;

  if (!issue) {
    return (
      <div className="mx-auto max-w-[1400px] bg-onyx px-4 py-32 text-center text-cream sm:px-6">
        <h1 className="font-display text-5xl uppercase text-metallic">The first issue is developing. 👀</h1>
        <CTA to="/campaigns" variant="pink" className="mt-8">Back to Archive</CTA>
      </div>
    );
  }

  return (
    <div className="bg-onyx text-cream">
      {/* 1. COVER */}
      <section className="grain-dark relative overflow-hidden border-b border-gold/25 bg-onyx" data-testid="issue-cover">
        <div className="halftone-gold pointer-events-none absolute inset-0 opacity-[0.04]" />
        <div className="relative mx-auto max-w-[1400px] px-4 py-14 sm:px-6 sm:py-20">
          <Link to="/campaigns" className="inline-flex items-center gap-2 font-head text-sm font-bold uppercase tracking-[0.15em] text-goldlight transition-colors hover:text-gold" data-testid="back-to-campaigns">
            <ArrowLeft size={16} /> Back to Archive
          </Link>
          <div className="mt-8 grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-silver/60">{issue.city} / {issue.season} · {issue.status}</p>
              <p className="mt-3 font-display text-[20vw] leading-[0.8] tracking-tight text-gold-metallic sm:text-[9rem]">
                ISSUE {issue.number}
              </p>
              <h1 className="mt-2 font-display text-4xl uppercase leading-[0.85] tracking-tight text-metallic sm:text-6xl">
                <MaskLines lines={issue.title.split(" ")} />
              </h1>
              <p className="mt-5 max-w-lg font-body text-lg leading-relaxed text-silver/85">{issue.summary}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {issue.disciplines.map((d) => (
                  <span key={d} className="border border-gold/30 bg-charcoal/60 px-3 py-1 font-mono text-xs uppercase tracking-wide text-goldlight/90">{d}</span>
                ))}
              </div>
              <div className="mt-8 flex items-end gap-6">
                <Barcode />
                <span className="font-mono text-[10px] uppercase tracking-widest text-silver/50">BLV-{issue.number} · COLLECT THEM ALL</span>
              </div>
            </div>
            <Reveal delay={0.2}>
              <div className="group panel-mono relative gold-frame overflow-hidden">
                <img src={issue.cover} alt={issue.title} className="h-[480px] w-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-onyx/70 to-transparent" />
                <span className="absolute bottom-3 left-3 -rotate-2 border border-gold/50 bg-onyx/85 px-2.5 py-0.5 font-marker text-xl text-goldlight">{issue.marker}</span>
              </div>
              <p className="mt-8 font-mono text-[10px] uppercase leading-relaxed tracking-widest text-silver/40">
                {issue.credits.map((c) => `${c.role}: ${c.name}`).join(" · ")}
              </p>
            </Reveal>
          </div>
        </div>
        <nav className="relative border-t border-gold/20 bg-charcoal" data-testid="issue-anchor-nav">
          <div className="mx-auto flex max-w-[1400px] gap-1 overflow-x-auto px-4 py-2 sm:px-6">
            {ANCHORS.map((a) => (
              <a key={a.id} href={`#${a.id}`} className="shrink-0 px-3 py-1.5 font-head text-xs font-extrabold uppercase tracking-wide text-silver/70 transition-colors hover:text-goldlight">
                #{a.label}
              </a>
            ))}
          </div>
        </nav>
      </section>

      {/* 2. BRIEF */}
      <IssueSection id="brief" kicker="The Brief">
        <p className="max-w-3xl font-head text-2xl font-bold leading-snug text-cream sm:text-3xl">{issue.brief}</p>
      </IssueSection>

      {/* 3. CONCEPT */}
      <IssueSection id="concept" kicker="The Concept" dark>
        <p className="max-w-3xl font-body text-lg leading-relaxed text-silver/85">{issue.concept}</p>
      </IssueSection>

      {/* 4. FINAL CAMPAIGN */}
      <IssueSection id="campaign" kicker="The Final Campaign">
        <p className="max-w-3xl font-body text-lg leading-relaxed text-silver/85">{issue.campaign}</p>
        <div className="mt-8 flex min-h-[180px] items-center justify-center border border-dashed border-gold/40 bg-charcoal/50 p-8 text-center">
          <p className="font-head text-lg font-extrabold uppercase text-silver/50">The first issue is developing. 👀<br /><span className="font-body text-sm font-medium normal-case">Final campaign work publishes here after production.</span></p>
        </div>

        {/* 5. CREATIVE BREAKDOWN */}
        <div className="mt-14">
          <Reveal><Kicker>Creative Breakdown</Kicker></Reveal>
          <div className="mt-6 space-y-4">
            {issue.breakdown.map((b) => (
              <Reveal key={b.anchor}>
                <div id={b.anchor} className="scroll-mt-24 grid gap-2 border border-silver/20 bg-charcoal/70 p-5 transition-colors hover:border-gold/40 md:grid-cols-[220px_1fr]">
                  <h3 className="font-display text-xl uppercase tracking-tight text-gold">#{b.anchor}<br /><span className="text-cream">{b.lane}</span></h3>
                  <p className="font-body text-sm leading-relaxed text-silver/80">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </IssueSection>

      {/* 6. MEET THE CREW */}
      <IssueSection id="crew" kicker="Meet the Crew" dark>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="issue-crew">
          {issue.crew.map((m) => {
            const c = castBySlug[m.slug];
            if (!c) return null;
            return (
              <Link key={m.slug} to={`/talent/${c.slug}`} className="group gold-frame bg-onyx/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(201,162,39,0.15)]" data-testid={`crew-${c.slug}`}>
                <div className="panel-mono h-48 overflow-hidden border-b border-gold/25">
                  <img src={c.img} alt={c.name} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-3">
                  <p className="font-display text-xl uppercase tracking-tight text-cream">{c.name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gold">{m.role}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </IssueSection>

      {/* 7. BEHIND THE ISSUE */}
      <IssueSection id="bts" kicker="Behind the Issue">
        <p className="max-w-3xl font-body text-lg leading-relaxed text-silver/85">{issue.bts}</p>
      </IssueSection>

      {/* 8. REFLECTION */}
      <IssueSection id="reflection" kicker="Creative Reflection" dark>
        <blockquote className="max-w-3xl">
          <p className="font-head text-2xl font-bold leading-snug text-cream sm:text-3xl">{issue.reflection.quote}</p>
          <cite className="mt-4 block font-marker text-xl not-italic text-goldlight">— {issue.reflection.by}</cite>
        </blockquote>
      </IssueSection>

      {/* 9. OUTCOMES + 10. PARTNERS + 11. CREDITS */}
      <IssueSection id="outcomes" kicker="Project Outcomes">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="issue-outcomes">
          {issue.outcomes.map((o) => (
            <div key={o.v} className="border border-silver/20 bg-charcoal/70 p-5">
              <span className="font-display text-5xl text-gold-metallic">{o.k}</span>
              <p className="mt-1 font-head text-xs font-extrabold uppercase tracking-wide text-cream">{o.v}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <div className="border border-silver/20 bg-charcoal/70 p-6">
            <h3 className="font-head text-lg font-extrabold uppercase tracking-tight text-cream">Partners</h3>
            <ul className="mt-3 space-y-1 font-body font-semibold text-silver/80">
              {issue.partners.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </div>
          <div id="credits" className="gold-frame scroll-mt-24 bg-onyx p-6">
            <h3 className="font-head text-lg font-extrabold uppercase tracking-tight text-goldlight">Full Credits</h3>
            <dl className="mt-3 space-y-1.5">
              {issue.credits.map((c) => (
                <div key={c.role} className="flex justify-between gap-4 font-body text-sm">
                  <dt className="font-mono text-[10px] uppercase tracking-widest text-silver/60">{c.role}</dt>
                  <dd className="font-bold text-cream">{c.name}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        {/* 12. NEXT ISSUE */}
        {next && next.slug !== issue.slug && (
          <Link to={`/issues/${next.slug}`} className="group mt-12 flex items-center justify-between gold-frame grain-dark bg-charcoal p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_35px_rgba(201,162,39,0.18)] sm:p-8" data-testid="next-issue-cta">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-gold">Next Issue</p>
              <p className="font-display text-3xl uppercase tracking-tight text-metallic sm:text-5xl">ISSUE {next.number} — {next.title}</p>
            </div>
            <ArrowRight size={32} className="shrink-0 text-gold transition-transform group-hover:translate-x-2" />
          </Link>
        )}
      </IssueSection>
    </div>
  );
}
